/**
 * Account Service - Multi-Tenant Account Management
 *
 * Provides CRUD operations for accounts, members, domains, and subscriptions.
 * All operations go through GraphQL API to the Rust backend.
 */

import { graphqlQuery, graphqlMutation } from '../database/graphqlClient';
import { genId } from '../domain/idPrefixes';
import type {
  Account,
  AccountId,
  AccountOverview,
  AccountMember,
  AccountMemberWithUser,
  AccountDomain,
  DomainId,
  Subscription,
  SubscriptionId,
  AccountInvitation,
  InvitationId,
  CreateAccountInput,
  UpdateAccountInput,
  InviteMemberInput,
  UpdateMemberInput,
  AddDomainInput,
  StorageUsage,
  AccountLimits,
  SlugValidation,
  AccountStatus,
  SubscriptionPlan,
  MemberRole,
} from '../domain/account';
import {
  calculateStorageUsage,
  validateSlugFormat,
  generateSlug,
  PLAN_STORAGE_QUOTA,
  PLAN_MAX_USERS,
} from '../domain/account';

// =============================================================================
// GRAPHQL FRAGMENTS
// =============================================================================

const ACCOUNT_FIELDS = `
  id
  name
  slug
  schemaName
  ownerId
  status
  plan
  storageQuotaBytes
  storageUsedBytes
  maxUsers
  currentUsers
  billingEmail
  customDomain
  trialEndsAt
  expiresAt
  settings
  createdAt
  updatedAt
  suspendedAt
  cancelledAt
`;

const ACCOUNT_OVERVIEW_FIELDS = `
  ${ACCOUNT_FIELDS}
  ownerName
  ownerEmail
  primaryDomain
  subscriptionPlan
  subscriptionStatus
  subscriptionEndsAt
  storageUsagePercent
`;

const ACCOUNT_MEMBER_FIELDS = `
  accountId
  userId
  role
  permissions
  isActive
  invitedBy
  invitedAt
  joinedAt
`;

const ACCOUNT_MEMBER_WITH_USER_FIELDS = `
  ${ACCOUNT_MEMBER_FIELDS}
  user {
    id
    email
    displayName
    avatarUrl
  }
`;

const ACCOUNT_DOMAIN_FIELDS = `
  id
  accountId
  domain
  domainType
  isPrimary
  sslStatus
  sslExpiresAt
  verificationToken
  verified
  verifiedAt
  createdAt
  updatedAt
`;

const SUBSCRIPTION_FIELDS = `
  id
  accountId
  plan
  status
  currentPeriodStart
  currentPeriodEnd
  monthlyPriceCents
  currency
  billingInterval
  stripeCustomerId
  stripeSubscriptionId
  stripePriceId
  trialStart
  trialEnd
  cancelAtPeriodEnd
  cancelledAt
  cancellationReason
  createdAt
  updatedAt
`;

const INVITATION_FIELDS = `
  id
  accountId
  email
  role
  token
  status
  invitedBy
  createdAt
  expiresAt
  acceptedAt
`;

// =============================================================================
// ACCOUNT CRUD
// =============================================================================

/**
 * Create a new account (tenant/workspace)
 */
export async function createAccount(input: CreateAccountInput): Promise<Account> {
  const id = genId('acc') as AccountId;

  const mutation = `
    mutation CreateAccount($input: CreateAccountInput!) {
      createAccount(input: $input) {
        ${ACCOUNT_FIELDS}
      }
    }
  `;

  const result = await graphqlMutation<{ createAccount: Account }>(mutation, {
    input: {
      id,
      ...input,
      plan: input.plan || 'starter',
    },
  });

  return result.createAccount;
}

/**
 * Get account by ID
 */
export async function getAccount(id: AccountId): Promise<Account | null> {
  const query = `
    query GetAccount($id: String!) {
      account(id: $id) {
        ${ACCOUNT_FIELDS}
      }
    }
  `;

  const result = await graphqlQuery<{ account: Account | null }>(query, { id });
  return result.account;
}

/**
 * Get account by slug (subdomain)
 */
export async function getAccountBySlug(slug: string): Promise<Account | null> {
  const query = `
    query GetAccountBySlug($slug: String!) {
      accountBySlug(slug: $slug) {
        ${ACCOUNT_FIELDS}
      }
    }
  `;

  const result = await graphqlQuery<{ accountBySlug: Account | null }>(query, { slug });
  return result.accountBySlug;
}

/**
 * Get account by domain (subdomain or custom domain)
 */
export async function getAccountByDomain(domain: string): Promise<Account | null> {
  const query = `
    query GetAccountByDomain($domain: String!) {
      accountByDomain(domain: $domain) {
        ${ACCOUNT_FIELDS}
      }
    }
  `;

  const result = await graphqlQuery<{ accountByDomain: Account | null }>(query, { domain });
  return result.accountByDomain;
}

/**
 * List all accounts (admin only)
 */
export async function listAccounts(options?: {
  status?: AccountStatus;
  plan?: SubscriptionPlan;
  limit?: number;
  offset?: number;
}): Promise<AccountOverview[]> {
  const query = `
    query ListAccounts($status: String, $plan: String, $limit: Int, $offset: Int) {
      accounts(status: $status, plan: $plan, limit: $limit, offset: $offset) {
        ${ACCOUNT_OVERVIEW_FIELDS}
      }
    }
  `;

  const result = await graphqlQuery<{ accounts: AccountOverview[] }>(query, options);
  return result.accounts || [];
}

/**
 * Update account
 */
export async function updateAccount(
  id: AccountId,
  input: UpdateAccountInput,
): Promise<Account> {
  const mutation = `
    mutation UpdateAccount($id: String!, $input: UpdateAccountInput!) {
      updateAccount(id: $id, input: $input) {
        ${ACCOUNT_FIELDS}
      }
    }
  `;

  const result = await graphqlMutation<{ updateAccount: Account }>(mutation, { id, input });
  return result.updateAccount;
}

/**
 * Suspend account
 */
export async function suspendAccount(id: AccountId, reason?: string): Promise<Account> {
  const mutation = `
    mutation SuspendAccount($id: String!, $reason: String) {
      suspendAccount(id: $id, reason: $reason) {
        ${ACCOUNT_FIELDS}
      }
    }
  `;

  const result = await graphqlMutation<{ suspendAccount: Account }>(mutation, { id, reason });
  return result.suspendAccount;
}

/**
 * Activate account (from trial or suspended)
 */
export async function activateAccount(id: AccountId): Promise<Account> {
  const mutation = `
    mutation ActivateAccount($id: String!) {
      activateAccount(id: $id) {
        ${ACCOUNT_FIELDS}
      }
    }
  `;

  const result = await graphqlMutation<{ activateAccount: Account }>(mutation, { id });
  return result.activateAccount;
}

/**
 * Cancel account
 */
export async function cancelAccount(id: AccountId, reason?: string): Promise<Account> {
  const mutation = `
    mutation CancelAccount($id: String!, $reason: String) {
      cancelAccount(id: $id, reason: $reason) {
        ${ACCOUNT_FIELDS}
      }
    }
  `;

  const result = await graphqlMutation<{ cancelAccount: Account }>(mutation, { id, reason });
  return result.cancelAccount;
}

/**
 * Delete account (permanent)
 */
export async function deleteAccount(id: AccountId): Promise<boolean> {
  const mutation = `
    mutation DeleteAccount($id: String!) {
      deleteAccount(id: $id)
    }
  `;

  const result = await graphqlMutation<{ deleteAccount: boolean }>(mutation, { id });
  return result.deleteAccount;
}

// =============================================================================
// ACCOUNT MEMBERS
// =============================================================================

/**
 * Get members of an account
 */
export async function getAccountMembers(accountId: AccountId): Promise<AccountMemberWithUser[]> {
  const query = `
    query GetAccountMembers($accountId: String!) {
      accountMembers(accountId: $accountId) {
        ${ACCOUNT_MEMBER_WITH_USER_FIELDS}
      }
    }
  `;

  const result = await graphqlQuery<{ accountMembers: AccountMemberWithUser[] }>(query, {
    accountId,
  });
  return result.accountMembers || [];
}

/**
 * Get accounts a user belongs to
 */
export async function getUserAccounts(userId: string): Promise<Account[]> {
  const query = `
    query GetUserAccounts($userId: String!) {
      userAccounts(userId: $userId) {
        ${ACCOUNT_FIELDS}
      }
    }
  `;

  const result = await graphqlQuery<{ userAccounts: Account[] }>(query, { userId });
  return result.userAccounts || [];
}

/**
 * Add member to account
 */
export async function addMember(
  accountId: AccountId,
  userId: string,
  role: MemberRole,
): Promise<AccountMember> {
  const mutation = `
    mutation AddAccountMember($accountId: String!, $userId: String!, $role: String!) {
      addAccountMember(accountId: $accountId, userId: $userId, role: $role) {
        ${ACCOUNT_MEMBER_FIELDS}
      }
    }
  `;

  const result = await graphqlMutation<{ addAccountMember: AccountMember }>(mutation, {
    accountId,
    userId,
    role,
  });
  return result.addAccountMember;
}

/**
 * Update member role/permissions
 */
export async function updateMember(
  accountId: AccountId,
  userId: string,
  input: UpdateMemberInput,
): Promise<AccountMember> {
  const mutation = `
    mutation UpdateAccountMember($accountId: String!, $userId: String!, $input: UpdateMemberInput!) {
      updateAccountMember(accountId: $accountId, userId: $userId, input: $input) {
        ${ACCOUNT_MEMBER_FIELDS}
      }
    }
  `;

  const result = await graphqlMutation<{ updateAccountMember: AccountMember }>(mutation, {
    accountId,
    userId,
    input,
  });
  return result.updateAccountMember;
}

/**
 * Remove member from account
 */
export async function removeMember(accountId: AccountId, userId: string): Promise<boolean> {
  const mutation = `
    mutation RemoveAccountMember($accountId: String!, $userId: String!) {
      removeAccountMember(accountId: $accountId, userId: $userId)
    }
  `;

  const result = await graphqlMutation<{ removeAccountMember: boolean }>(mutation, {
    accountId,
    userId,
  });
  return result.removeAccountMember;
}

// =============================================================================
// INVITATIONS
// =============================================================================

/**
 * Invite a user to join an account
 */
export async function inviteMember(
  accountId: AccountId,
  input: InviteMemberInput,
): Promise<AccountInvitation> {
  const id = genId('inv') as InvitationId;

  const mutation = `
    mutation InviteMember($accountId: String!, $input: InviteMemberInput!) {
      inviteMember(accountId: $accountId, input: $input) {
        ${INVITATION_FIELDS}
      }
    }
  `;

  const result = await graphqlMutation<{ inviteMember: AccountInvitation }>(mutation, {
    accountId,
    input: { id, ...input },
  });
  return result.inviteMember;
}

/**
 * Get pending invitations for an account
 */
export async function getAccountInvitations(accountId: AccountId): Promise<AccountInvitation[]> {
  const query = `
    query GetAccountInvitations($accountId: String!) {
      accountInvitations(accountId: $accountId) {
        ${INVITATION_FIELDS}
      }
    }
  `;

  const result = await graphqlQuery<{ accountInvitations: AccountInvitation[] }>(query, {
    accountId,
  });
  return result.accountInvitations || [];
}

/**
 * Accept invitation
 */
export async function acceptInvitation(token: string): Promise<AccountMember> {
  const mutation = `
    mutation AcceptInvitation($token: String!) {
      acceptInvitation(token: $token) {
        ${ACCOUNT_MEMBER_FIELDS}
      }
    }
  `;

  const result = await graphqlMutation<{ acceptInvitation: AccountMember }>(mutation, { token });
  return result.acceptInvitation;
}

/**
 * Revoke invitation
 */
export async function revokeInvitation(id: InvitationId): Promise<boolean> {
  const mutation = `
    mutation RevokeInvitation($id: String!) {
      revokeInvitation(id: $id)
    }
  `;

  const result = await graphqlMutation<{ revokeInvitation: boolean }>(mutation, { id });
  return result.revokeInvitation;
}

// =============================================================================
// DOMAINS
// =============================================================================

/**
 * Get domains for an account
 */
export async function getAccountDomains(accountId: AccountId): Promise<AccountDomain[]> {
  const query = `
    query GetAccountDomains($accountId: String!) {
      accountDomains(accountId: $accountId) {
        ${ACCOUNT_DOMAIN_FIELDS}
      }
    }
  `;

  const result = await graphqlQuery<{ accountDomains: AccountDomain[] }>(query, { accountId });
  return result.accountDomains || [];
}

/**
 * Add custom domain to account
 */
export async function addDomain(
  accountId: AccountId,
  input: AddDomainInput,
): Promise<AccountDomain> {
  const id = genId('dom') as DomainId;

  const mutation = `
    mutation AddAccountDomain($accountId: String!, $input: AddDomainInput!) {
      addAccountDomain(accountId: $accountId, input: $input) {
        ${ACCOUNT_DOMAIN_FIELDS}
      }
    }
  `;

  const result = await graphqlMutation<{ addAccountDomain: AccountDomain }>(mutation, {
    accountId,
    input: { id, ...input },
  });
  return result.addAccountDomain;
}

/**
 * Verify custom domain ownership
 */
export async function verifyDomain(id: DomainId): Promise<AccountDomain> {
  const mutation = `
    mutation VerifyDomain($id: String!) {
      verifyDomain(id: $id) {
        ${ACCOUNT_DOMAIN_FIELDS}
      }
    }
  `;

  const result = await graphqlMutation<{ verifyDomain: AccountDomain }>(mutation, { id });
  return result.verifyDomain;
}

/**
 * Remove domain from account
 */
export async function removeDomain(id: DomainId): Promise<boolean> {
  const mutation = `
    mutation RemoveAccountDomain($id: String!) {
      removeAccountDomain(id: $id)
    }
  `;

  const result = await graphqlMutation<{ removeAccountDomain: boolean }>(mutation, { id });
  return result.removeAccountDomain;
}

/**
 * Set primary domain
 */
export async function setPrimaryDomain(id: DomainId): Promise<AccountDomain> {
  const mutation = `
    mutation SetPrimaryDomain($id: String!) {
      setPrimaryDomain(id: $id) {
        ${ACCOUNT_DOMAIN_FIELDS}
      }
    }
  `;

  const result = await graphqlMutation<{ setPrimaryDomain: AccountDomain }>(mutation, { id });
  return result.setPrimaryDomain;
}

// =============================================================================
// SUBSCRIPTIONS
// =============================================================================

/**
 * Get subscription for an account
 */
export async function getSubscription(accountId: AccountId): Promise<Subscription | null> {
  const query = `
    query GetSubscription($accountId: String!) {
      subscription(accountId: $accountId) {
        ${SUBSCRIPTION_FIELDS}
      }
    }
  `;

  const result = await graphqlQuery<{ subscription: Subscription | null }>(query, { accountId });
  return result.subscription;
}

/**
 * Update subscription plan
 */
export async function updateSubscriptionPlan(
  accountId: AccountId,
  plan: SubscriptionPlan,
): Promise<Subscription> {
  const mutation = `
    mutation UpdateSubscriptionPlan($accountId: String!, $plan: String!) {
      updateSubscriptionPlan(accountId: $accountId, plan: $plan) {
        ${SUBSCRIPTION_FIELDS}
      }
    }
  `;

  const result = await graphqlMutation<{ updateSubscriptionPlan: Subscription }>(mutation, {
    accountId,
    plan,
  });
  return result.updateSubscriptionPlan;
}

/**
 * Cancel subscription at period end
 */
export async function cancelSubscription(
  accountId: AccountId,
  reason?: string,
): Promise<Subscription> {
  const mutation = `
    mutation CancelSubscription($accountId: String!, $reason: String) {
      cancelSubscription(accountId: $accountId, reason: $reason) {
        ${SUBSCRIPTION_FIELDS}
      }
    }
  `;

  const result = await graphqlMutation<{ cancelSubscription: Subscription }>(mutation, {
    accountId,
    reason,
  });
  return result.cancelSubscription;
}

// =============================================================================
// STORAGE
// =============================================================================

/**
 * Get storage usage for an account
 */
export async function getStorageUsage(accountId: AccountId): Promise<StorageUsage> {
  const account = await getAccount(accountId);
  if (!account) {
    throw new Error(`Account ${accountId} not found`);
  }
  return calculateStorageUsage(account);
}

/**
 * Update storage usage for an account
 */
export async function updateStorageUsage(
  accountId: AccountId,
  usedBytes: number,
): Promise<Account> {
  const mutation = `
    mutation UpdateStorageUsage($accountId: String!, $usedBytes: Int!) {
      updateStorageUsage(accountId: $accountId, usedBytes: $usedBytes) {
        ${ACCOUNT_FIELDS}
      }
    }
  `;

  const result = await graphqlMutation<{ updateStorageUsage: Account }>(mutation, {
    accountId,
    usedBytes,
  });
  return result.updateStorageUsage;
}

/**
 * Check if account has enough storage quota for additional bytes
 */
export async function checkStorageQuota(
  accountId: AccountId,
  additionalBytes: number,
): Promise<boolean> {
  const account = await getAccount(accountId);
  if (!account) {
    return false;
  }
  return account.storageUsedBytes + additionalBytes <= account.storageQuotaBytes;
}

/**
 * Get account limits
 */
export async function getAccountLimits(accountId: AccountId): Promise<AccountLimits> {
  const account = await getAccount(accountId);
  if (!account) {
    throw new Error(`Account ${accountId} not found`);
  }

  return {
    storage: calculateStorageUsage(account),
    users: {
      current: account.currentUsers,
      max: account.maxUsers,
      available: Math.max(0, account.maxUsers - account.currentUsers),
      isAtLimit: account.currentUsers >= account.maxUsers,
    },
  };
}

// =============================================================================
// SLUG VALIDATION
// =============================================================================

/**
 * Validate slug availability
 */
export async function validateSlug(slug: string): Promise<SlugValidation> {
  // First check format
  const formatValidation = validateSlugFormat(slug);
  if (!formatValidation.valid) {
    return {
      isValid: false,
      isAvailable: false,
      error: slug.length < 3 ? 'too_short' : slug.length > 50 ? 'too_long' : 'invalid_format',
    };
  }

  // Check availability via GraphQL
  const query = `
    query IsSlugAvailable($slug: String!) {
      isSlugAvailable(slug: $slug)
    }
  `;

  const result = await graphqlQuery<{ isSlugAvailable: boolean }>(query, { slug });

  if (!result.isSlugAvailable) {
    // Generate suggestion
    const suggestion = `${slug}-${Math.random().toString(36).slice(2, 6)}`;
    return {
      isValid: true,
      isAvailable: false,
      error: 'taken',
      suggestion,
    };
  }

  return {
    isValid: true,
    isAvailable: true,
  };
}

/**
 * Generate and validate a slug from a name
 */
export async function generateValidSlug(name: string): Promise<string> {
  let slug = generateSlug(name);
  let attempt = 0;

  while (attempt < 10) {
    const validation = await validateSlug(slug);
    if (validation.isAvailable) {
      return slug;
    }
    // Add random suffix
    slug = `${generateSlug(name)}-${Math.random().toString(36).slice(2, 6)}`;
    attempt++;
  }

  // Fallback to timestamp-based slug
  return `${generateSlug(name)}-${Date.now().toString(36)}`;
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Get plan limits for a specific plan
 */
export function getPlanLimits(plan: SubscriptionPlan): {
  storageBytes: number;
  maxUsers: number;
} {
  return {
    storageBytes: PLAN_STORAGE_QUOTA[plan],
    maxUsers: PLAN_MAX_USERS[plan],
  };
}

/**
 * Check if user is owner of account
 */
export async function isAccountOwner(accountId: AccountId, userId: string): Promise<boolean> {
  const account = await getAccount(accountId);
  return account?.ownerId === userId;
}

/**
 * Check if user is admin of account
 */
export async function isAccountAdmin(accountId: AccountId, userId: string): Promise<boolean> {
  const members = await getAccountMembers(accountId);
  const member = members.find(m => m.userId === userId);
  return member?.role === 'owner' || member?.role === 'admin';
}

/**
 * Get user's role in an account
 */
export async function getUserRole(
  accountId: AccountId,
  userId: string,
): Promise<MemberRole | null> {
  const members = await getAccountMembers(accountId);
  const member = members.find(m => m.userId === userId);
  return member?.role || null;
}
